import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";
import {
  Building2,
  Clock3,
  LocateFixed,
  MapPinned,
  Radio,
  RefreshCw,
  Route,
  Truck,
  UserRound,
  WifiOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button, EmptyState, Notice, SelectField } from "../../components/ui";
import { api } from "../../lib/api";
import {
  createTripLocationSocket,
  parseTripLocationSocketMessage,
} from "../../lib/tripLocationSocket";
import type {
  AdminFleetResponse,
  AdminFleetTrip,
  AdminTripLocationEvent,
  FleetTrackingStatus,
} from "../../types";
import { TripLocationDialog } from "./TripLocationDialog";

type Coordinate = [number, number];
type SelectedConnectionStatus = "idle" | "connecting" | "live" | "reconnecting" | "fallback";

const FLEET_REFRESH_INTERVAL_MS = 60_000;
const SOCKET_HANDSHAKE_TIMEOUT_MS = 10_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

const trackingPresentation: Record<
  FleetTrackingStatus,
  { label: string; description: string; badge: string; color: string }
> = {
  LIVE: {
    label: "Canlı",
    description: "Son konum 2 dakika içinde alındı",
    badge: "bg-emerald-50 text-emerald-700",
    color: "#10b981",
  },
  STALE: {
    label: "Gecikmiş",
    description: "Son konum 2–10 dakika önce alındı",
    badge: "bg-amber-50 text-amber-700",
    color: "#f59e0b",
  },
  OFFLINE: {
    label: "Offline",
    description: "10 dakikadan uzun süredir konum yok",
    badge: "bg-red-50 text-red-700",
    color: "#ef4444",
  },
  TRACKING_OFF: {
    label: "Takip kapalı",
    description: "Sefer henüz başlatılmadı",
    badge: "bg-slate-100 text-slate-600",
    color: "#64748b",
  },
  NO_DATA: {
    label: "Konum yok",
    description: "Aktif seferden konum alınmadı; izin veya cihaz durumu doğrulanamıyor",
    badge: "bg-violet-50 text-violet-700",
    color: "#8b5cf6",
  },
};

const connectionPresentation: Record<
  SelectedConnectionStatus,
  { label: string; tone: string }
> = {
  idle: { label: "Sefer seçilmedi", tone: "bg-slate-100 text-slate-600" },
  connecting: { label: "Canlı bağlantı kuruluyor", tone: "bg-amber-50 text-amber-700" },
  live: { label: "Seçili sefer canlı", tone: "bg-emerald-50 text-emerald-700" },
  reconnecting: { label: "Yeniden bağlanıyor", tone: "bg-amber-50 text-amber-700" },
  fallback: { label: "HTTP yedek aktif", tone: "bg-sky-50 text-sky-700" },
};

const dateTimeFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function formatDateTime(value: string) {
  return dateTimeFormatter.format(new Date(value));
}

function formatRelativeTime(value: string, now: number) {
  const differenceSeconds = Math.round((new Date(value).getTime() - now) / 1000);
  const formatter = new Intl.RelativeTimeFormat("tr-TR", { numeric: "auto" });
  if (Math.abs(differenceSeconds) < 60) return formatter.format(differenceSeconds, "second");
  const minutes = Math.round(differenceSeconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

function currentTrackingStatus(
  trip: AdminFleetTrip,
  thresholds: AdminFleetResponse["thresholds"],
  now: number,
): FleetTrackingStatus {
  if (trip.status === "PLANNED") return "TRACKING_OFF";
  if (!trip.latestLocation) return "NO_DATA";
  const ageSeconds = Math.max(0, (now - new Date(trip.latestLocation.recordedAt).getTime()) / 1000);
  if (ageSeconds <= thresholds.liveSeconds) return "LIVE";
  if (ageSeconds <= thresholds.offlineSeconds) return "STALE";
  return "OFFLINE";
}

function latestLocationFromMessage(
  locations: Array<AdminTripLocationEvent & { tripId?: string }>,
) {
  return locations.reduce<AdminTripLocationEvent | null>((latest, location) => {
    if (!latest) return location;
    return new Date(location.recordedAt).getTime() > new Date(latest.recordedAt).getTime()
      ? location
      : latest;
  }, null);
}

function MapViewport({ points, selectedPoint, fitKey }: {
  points: Coordinate[];
  selectedPoint: Coordinate | null;
  fitKey: string;
}) {
  const map = useMap();
  const pointsRef = useRef(points);
  pointsRef.current = points;

  useEffect(() => {
    const currentPoints = pointsRef.current;
    if (currentPoints.length === 0) return;
    if (currentPoints.length === 1) map.setView(currentPoints[0], 14);
    else map.fitBounds(currentPoints, { padding: [44, 44], maxZoom: 14 });
  }, [fitKey, map]);

  useEffect(() => {
    if (selectedPoint) map.flyTo(selectedPoint, Math.max(map.getZoom(), 14), { duration: 0.45 });
  }, [map, selectedPoint]);

  return null;
}

function TrackingBadge({ status }: { status: FleetTrackingStatus }) {
  const presentation = trackingPresentation[status];
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${presentation.badge}`}>
      <span className={`h-2 w-2 rounded-full bg-current ${status === "LIVE" ? "animate-pulse" : ""}`} />
      {presentation.label}
    </span>
  );
}

export function FleetMapPage() {
  const [data, setData] = useState<AdminFleetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [detailTripId, setDetailTripId] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<SelectedConnectionStatus>("idle");
  const [now, setNow] = useState(Date.now());
  const [driverFilter, setDriverFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [tripStatusFilter, setTripStatusFilter] = useState("");
  const [trackingFilter, setTrackingFilter] = useState("");
  const loadInFlightRef = useRef<Promise<void> | null>(null);

  const load = useCallback((background = false) => {
    if (loadInFlightRef.current) return loadInFlightRef.current;
    const request = (async () => {
      if (background) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await api<AdminFleetResponse>("/admin/fleet");
        setData(response);
        setError("");
        setNow(Date.now());
        setSelectedTripId((current) => current && response.trips.some((trip) => trip.id === current) ? current : null);
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Filo konumları yüklenemedi.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    })();
    loadInFlightRef.current = request;
    void request.finally(() => {
      if (loadInFlightRef.current === request) loadInFlightRef.current = null;
    });
    return request;
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(true);
    }, FLEET_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const selectedTrip = data?.trips.find((trip) => trip.id === selectedTripId) ?? null;

  const selectedTripStatus = selectedTrip?.status ?? null;

  useEffect(() => {
    if (!selectedTripId || selectedTripStatus !== "ACTIVE" || detailTripId) {
      setConnectionStatus("idle");
      return;
    }

    let disposed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let handshakeTimer: number | null = null;
    let retryAttempt = 0;

    const clearHandshakeTimer = () => {
      if (handshakeTimer !== null) window.clearTimeout(handshakeTimer);
      handshakeTimer = null;
    };
    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) return;
      setConnectionStatus("fallback");
      const baseDelay = Math.min(MAX_RECONNECT_DELAY_MS, 1_000 * 2 ** Math.min(retryAttempt, 5));
      const delay = Math.round(baseDelay * (0.75 + Math.random() * 0.5));
      retryAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect(true);
      }, delay);
    };
    const connect = (reconnecting: boolean) => {
      if (disposed) return;
      setConnectionStatus(reconnecting ? "reconnecting" : "connecting");
      try {
        socket = createTripLocationSocket(selectedTripId, "admin");
      } catch {
        scheduleReconnect();
        return;
      }
      const activeSocket = socket;
      let ready = false;
      handshakeTimer = window.setTimeout(() => {
        if (!disposed && !ready) activeSocket.close(4000, "Canlı bağlantı zaman aşımı");
      }, SOCKET_HANDSHAKE_TIMEOUT_MS);
      activeSocket.onmessage = (event) => {
        if (disposed) return;
        const message = parseTripLocationSocketMessage(event.data);
        if (!message || message.tripId !== selectedTripId) {
          if (message && message.tripId !== selectedTripId) activeSocket.close(1008, "Sefer eşleşmedi");
          return;
        }
        if (!ready) {
          ready = true;
          retryAttempt = 0;
          clearHandshakeTimer();
          setConnectionStatus("live");
        }
        if (message.type !== "trip.location.batch") return;
        const latest = latestLocationFromMessage(message.locations);
        if (!latest) return;
        setNow(Date.now());
        setData((current) => current ? {
          ...current,
          trips: current.trips.map((trip) => trip.id === message.tripId
            ? { ...trip, trackingStatus: "LIVE", latestLocation: latest }
            : trip),
        } : current);
      };
      activeSocket.onerror = () => {
        if (!disposed) activeSocket.close();
      };
      activeSocket.onclose = () => {
        clearHandshakeTimer();
        if (socket === activeSocket) socket = null;
        if (!disposed) scheduleReconnect();
      };
    };

    connect(false);
    return () => {
      disposed = true;
      clearHandshakeTimer();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close(1000, "Sefer seçimi değişti");
      }
    };
  }, [detailTripId, selectedTripId, selectedTripStatus]);

  const tripsWithStatus = useMemo(() => (data?.trips ?? []).map((trip) => ({
    trip,
    trackingStatus: currentTrackingStatus(
      trip,
      data?.thresholds ?? { liveSeconds: 120, offlineSeconds: 600 },
      now,
    ),
  })), [data, now]);

  const drivers = useMemo(() => [...new Map((data?.trips ?? []).map((trip) => [trip.driver.id, trip.driver])).values()]
    .sort((left, right) => left.fullName.localeCompare(right.fullName, "tr")), [data]);
  const vehicles = useMemo(() => [...new Map((data?.trips ?? []).filter((trip) => trip.vehicle).map((trip) => [trip.vehicle!.id, trip.vehicle!])).values()]
    .sort((left, right) => left.plateNumber.localeCompare(right.plateNumber, "tr")), [data]);
  const companies = useMemo(() => [...new Map((data?.trips ?? []).map((trip) => [trip.assignment.quote.organization.id, trip.assignment.quote.organization])).values()]
    .sort((left, right) => left.name.localeCompare(right.name, "tr")), [data]);

  const filteredTrips = useMemo(() => tripsWithStatus.filter(({ trip, trackingStatus }) =>
    (!driverFilter || trip.driver.id === driverFilter)
    && (!vehicleFilter || trip.vehicle?.id === vehicleFilter)
    && (!companyFilter || trip.assignment.quote.organization.id === companyFilter)
    && (!tripStatusFilter || trip.status === tripStatusFilter)
    && (!trackingFilter || trackingStatus === trackingFilter)), [
    companyFilter,
    driverFilter,
    trackingFilter,
    tripStatusFilter,
    tripsWithStatus,
    vehicleFilter,
  ]);

  const statusCounts = useMemo(() => Object.fromEntries(
    (Object.keys(trackingPresentation) as FleetTrackingStatus[]).map((status) => [
      status,
      tripsWithStatus.filter((item) => item.trackingStatus === status).length,
    ]),
  ) as Record<FleetTrackingStatus, number>, [tripsWithStatus]);

  const markerTrips = filteredTrips.filter(({ trip }) => trip.latestLocation);
  const mapPoints = markerTrips.map(({ trip }) => [
    trip.latestLocation!.latitude,
    trip.latestLocation!.longitude,
  ] as Coordinate);
  const selectedPoint: Coordinate | null = selectedTrip?.latestLocation
    ? [selectedTrip.latestLocation.latitude, selectedTrip.latestLocation.longitude]
    : null;
  const fitKey = filteredTrips.map(({ trip }) => trip.id).sort().join(":");
  const activeFilters = [driverFilter, vehicleFilter, companyFilter, tripStatusFilter, trackingFilter].filter(Boolean).length;
  const selectedConnection = connectionPresentation[connectionStatus];

  const resetFilters = () => {
    setDriverFilter("");
    setVehicleFilter("");
    setCompanyFilter("");
    setTripStatusFilter("");
    setTrackingFilter("");
  };

  return (
    <>
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-navy">Canlı Filo</h1>
          <p className="mt-2 text-sm text-slate-500">Aktif ve planlanan operasyonların son konumunu tek ekrandan izleyin.</p>
        </div>
        <Button type="button" variant="ghost" loading={refreshing} onClick={() => void load(true)}>
          <RefreshCw size={16} /> Yenile
        </Button>
      </div>

      {error && data && <Notice tone="warning" className="mb-5">Son filo yenilemesi başarısız: {error}</Notice>}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {(Object.keys(trackingPresentation) as FleetTrackingStatus[]).map((status) => {
          const presentation = trackingPresentation[status];
          return (
            <button
              type="button"
              key={status}
              aria-pressed={trackingFilter === status}
              onClick={() => setTrackingFilter((current) => current === status ? "" : status)}
              className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-card ${trackingFilter === status ? "border-teal ring-4 ring-teal/10" : "border-slate-100"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <TrackingBadge status={status} />
                <span className="text-2xl font-bold text-navy">{statusCounts[status]}</span>
              </div>
              <p className="mt-3 text-xs leading-5 text-slate-500">{presentation.description}</p>
            </button>
          );
        })}
      </div>

      <section className="card mb-5 p-5" aria-label="Filo filtreleri">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <SelectField label="Şoför" value={driverFilter} onChange={(event) => setDriverFilter(event.target.value)}>
            <option value="">Tüm şoförler</option>
            {drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.fullName}</option>)}
          </SelectField>
          <SelectField label="Araç" value={vehicleFilter} onChange={(event) => setVehicleFilter(event.target.value)}>
            <option value="">Tüm araçlar</option>
            {vehicles.map((vehicle) => <option key={vehicle.id} value={vehicle.id}>{vehicle.plateNumber} · {vehicle.brand} {vehicle.model}</option>)}
          </SelectField>
          <SelectField label="Firma" value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)}>
            <option value="">Tüm firmalar</option>
            {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
          </SelectField>
          <SelectField label="Sefer" value={tripStatusFilter} onChange={(event) => setTripStatusFilter(event.target.value)}>
            <option value="">Tüm seferler</option>
            <option value="ACTIVE">Aktif</option>
            <option value="PLANNED">Planlandı</option>
          </SelectField>
          <SelectField label="Takip" value={trackingFilter} onChange={(event) => setTrackingFilter(event.target.value)}>
            <option value="">Tüm takip durumları</option>
            {(Object.keys(trackingPresentation) as FleetTrackingStatus[]).map((status) => <option key={status} value={status}>{trackingPresentation[status].label}</option>)}
          </SelectField>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs text-slate-500">
          <p>{filteredTrips.length} / {data?.trips.length ?? 0} operasyon gösteriliyor{activeFilters ? ` · ${activeFilters} filtre aktif` : ""}</p>
          {activeFilters > 0 && <button type="button" onClick={resetFilters} className="font-bold text-teal hover:underline">Filtreleri temizle</button>}
        </div>
      </section>

      {loading && !data ? (
        <div className="card grid min-h-[520px] place-items-center text-sm font-semibold text-slate-500">
          <div className="text-center"><RefreshCw className="mx-auto mb-3 animate-spin text-teal" /> Filo konumları yükleniyor…</div>
        </div>
      ) : error && !data ? (
        <div className="card space-y-4 p-6">
          <Notice tone="error">{error}</Notice>
          <Button type="button" variant="secondary" onClick={() => void load()}>Tekrar Dene</Button>
        </div>
      ) : data && data.trips.length === 0 ? (
        <EmptyState title="Aktif operasyon bulunamadı" text="Planlanan veya devam eden bir sefer oluştuğunda filo haritasında görünecek." />
      ) : data ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(340px,.5fr)]">
          <section className="card overflow-hidden" aria-label="Filo haritası">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
              <div>
                <h2 className="flex items-center gap-2 font-bold text-navy"><MapPinned size={19} className="text-teal" /> Operasyon Haritası</h2>
                <p className="mt-1 text-xs text-slate-500">Genel özet 60 saniyede bir yenilenir; yalnız seçili aktif sefer tek WebSocket ile canlı izlenir.</p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${selectedConnection.tone}`}>
                  <Radio size={13} className={connectionStatus === "live" ? "animate-pulse" : ""} /> {selectedConnection.label}
                </span>
                <p className="mt-1 text-[11px] text-slate-400">Özet: {formatDateTime(data.generatedAt)}</p>
              </div>
            </div>
            <div className="relative h-[560px] bg-[#dce9e5]">
              <MapContainer center={[39.1, 35.2]} zoom={6} scrollWheelZoom className="absolute inset-0">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> katkıcıları'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {markerTrips.map(({ trip, trackingStatus }) => {
                  const location = trip.latestLocation!;
                  const presentation = trackingPresentation[trackingStatus];
                  const selected = trip.id === selectedTripId;
                  return (
                    <CircleMarker
                      key={trip.id}
                      center={[location.latitude, location.longitude]}
                      radius={selected ? 12 : 9}
                      pathOptions={{ color: "#fff", weight: selected ? 5 : 3, fillColor: presentation.color, fillOpacity: 1 }}
                      eventHandlers={{ click: () => setSelectedTripId(trip.id) }}
                    >
                      <Popup>
                        <div className="min-w-52 space-y-2">
                          <div className="flex items-center justify-between gap-3"><strong>{trip.assignment.quote.quoteNumber}</strong><span>{presentation.label}</span></div>
                          <p>{trip.driver.fullName} · {trip.vehicle?.plateNumber ?? "Araç seçilmedi"}</p>
                          <p>{trip.assignment.quote.organization.name}</p>
                          <p className="text-xs">Son konum: {formatRelativeTime(location.recordedAt, now)}</p>
                          <button type="button" onClick={() => setDetailTripId(trip.id)} className="font-semibold text-teal">Sefer ve rota detayını aç</button>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
                <MapViewport points={mapPoints} selectedPoint={selectedPoint} fitKey={fitKey} />
              </MapContainer>
              {markerTrips.length === 0 && (
                <div className="pointer-events-none absolute left-4 top-4 z-[500] max-w-xs rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                  <p className="flex items-center gap-2 font-semibold text-navy"><LocateFixed size={17} className="text-coral" /> Filtrede konum bulunamadı</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">Konumu olmayan operasyonlar sağdaki listede “Konum yok” durumuyla görünmeye devam eder; izin veya cihaz nedeni sunucudan kesinleştirilemez.</p>
                </div>
              )}
            </div>
          </section>

          <aside className="card flex max-h-[650px] min-h-[520px] flex-col overflow-hidden" aria-label="Operasyon listesi">
            <div className="border-b p-5">
              <h2 className="font-bold text-navy">Operasyonlar</h2>
              <p className="mt-1 text-xs text-slate-500">Haritada odaklanmak için bir sefer seçin.</p>
            </div>
            {filteredTrips.length === 0 ? (
              <div className="p-5"><EmptyState title="Filtreye uygun sefer yok" text="Filtreleri değiştirerek diğer operasyonları görüntüleyin." /></div>
            ) : (
              <div className="space-y-3 overflow-y-auto p-4">
                {filteredTrips.map(({ trip, trackingStatus }) => {
                  const selected = selectedTripId === trip.id;
                  return (
                    <article key={trip.id} className={`rounded-2xl border p-4 transition ${selected ? "border-teal bg-teal/5 shadow-sm" : "border-slate-200 bg-white"}`}>
                      <button type="button" aria-pressed={selected} onClick={() => setSelectedTripId(trip.id)} className="w-full text-left">
                        <div className="flex items-start justify-between gap-3">
                          <div><p className="font-bold text-navy">{trip.assignment.quote.quoteNumber}</p><p className="mt-1 text-xs text-slate-500">{trip.assignment.quote.organization.name}</p></div>
                          <TrackingBadge status={trackingStatus} />
                        </div>
                        <div className="mt-4 space-y-2 text-sm text-slate-600">
                          <p className="flex items-center gap-2"><UserRound size={15} className="text-teal" /> {trip.driver.fullName}</p>
                          <p className="flex items-center gap-2"><Truck size={15} className="text-coral" /> {trip.vehicle ? `${trip.vehicle.plateNumber} · ${trip.vehicle.brand} ${trip.vehicle.model}` : `${trip.assignment.quote.vehicleType.name} · Araç seçilmedi`}</p>
                          <p className="flex items-center gap-2"><Route size={15} className="text-slate-400" /> <span className="truncate">{trip.assignment.quote.originLabel} → {trip.assignment.quote.destinationLabel}</span></p>
                          <p className="flex items-center gap-2 text-xs text-slate-400">
                            {trip.latestLocation ? <Clock3 size={14} /> : <WifiOff size={14} />}
                            {trip.latestLocation ? `Son konum ${formatRelativeTime(trip.latestLocation.recordedAt, now)}` : trackingPresentation[trackingStatus].description}
                          </p>
                        </div>
                      </button>
                      <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3 text-center text-xs font-semibold">
                        <Link to={`/admin/teklifler?detay=${encodeURIComponent(trip.assignment.quote.id)}`} className="rounded-lg px-2 py-2 text-teal hover:bg-white"><Building2 size={14} className="mx-auto mb-1" /> Teklif</Link>
                        <Link to={`/admin/soforler?detay=${encodeURIComponent(trip.driver.id)}`} className="rounded-lg px-2 py-2 text-teal hover:bg-white"><UserRound size={14} className="mx-auto mb-1" /> Şoför</Link>
                        <button type="button" onClick={() => setDetailTripId(trip.id)} className="rounded-lg px-2 py-2 text-teal hover:bg-white"><MapPinned size={14} className="mx-auto mb-1" /> Sefer</button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </aside>
        </div>
      ) : null}

      <TripLocationDialog tripId={detailTripId} audience="admin" onClose={() => setDetailTripId(null)} />
    </>
  );
}
